import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { useEffect, useState } from 'react';
import { fetchMe } from './api';

import { navigateTo, useAppPathname } from './router';
import { DocsPage } from './pages/DocsPage';
import { WhyPage } from './pages/WhyPage';
import { ArgumentPage } from './pages/ArgumentPage';
import { HomePage } from './pages/HomePage';
import { SearchPage } from './pages/SearchPage';
import { DashboardPage } from './pages/DashboardPage';
import { RequestIndexPage } from './pages/RequestIndexPage';
import { RawLlmTxtPage } from './pages/RawLlmTxtPage';
import { QueryPage } from './pages/QueryPage';
import { OrgSettingsPage } from './pages/OrgSettingsPage';
import { CreateOrgPage } from './pages/CreateOrgPage';
import { PackagesListPage } from './pages/PackagesListPage';
import { PackageDetailPage } from './pages/PackageDetailPage';
import { IndexTreePage } from './pages/IndexTreePage';
import { StatusPage } from './pages/StatusPage';
import { ContactPage } from './pages/ContactPage';
import type { LcRouteParams } from './types';

type LcRoute =
  | { kind: 'docs' }
  | { kind: 'why' }
  | { kind: 'argument' }
  | { kind: 'home' }
  | { kind: 'search' }
  | { kind: 'contact' }
  | { kind: 'library'; params: LcRouteParams }
  | { kind: 'index-tree'; params: LcRouteParams }
  | { kind: 'dashboard' }
  | { kind: 'request' }
  | { kind: 'skills' }
  | { kind: 'status' }
  | { kind: 'raw'; params: LcRouteParams }
  | { kind: 'query'; params: LcRouteParams }
  | { kind: 'org-settings' }
  | { kind: 'org-new' }
  | { kind: 'admin' }
  | { kind: 'not-found' };

function parseLcRoute(pathname: string): LcRoute {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return { kind: 'home' };
  }

  if (segments.length === 1 && segments[0] === 'docs') {
    return { kind: 'docs' };
  }

  if (segments.length === 1 && segments[0] === 'why') {
    return { kind: 'why' };
  }

  if (segments.length === 1 && segments[0] === 'argument') {
    return { kind: 'argument' };
  }

  if (segments.length === 1 && segments[0] === 'search') {
    return { kind: 'search' };
  }

  if (segments.length === 1 && segments[0] === 'contact') {
    return { kind: 'contact' };
  }

  if (segments.length === 1 && segments[0] === 'dashboard') {
    return { kind: 'dashboard' };
  }

  if (segments.length === 1 && segments[0] === 'request') {
    return { kind: 'request' };
  }

  if (segments.length === 1 && segments[0] === 'skills') {
    return { kind: 'skills' };
  }

  // /status renders the last site_check.py run. Not linked from the nav — it is
  // an operational view, reachable by URL.
  if (segments.length === 1 && segments[0] === 'status') {
    return { kind: 'status' };
  }

  // Legacy /packages routes redirect to home
  if (segments[0] === 'packages') {
    return { kind: 'home' };
  }

  if (segments.length === 2 && segments[0] === 'org' && segments[1] === 'settings') {
    return { kind: 'org-settings' };
  }

  if (segments.length === 2 && segments[0] === 'org' && segments[1] === 'new') {
    return { kind: 'org-new' };
  }

  if (segments.length === 1 && segments[0] === 'admin') {
    return { kind: 'admin' };
  }

  if (segments.length === 4) {
    const params = {
      registry: decodeURIComponent(segments[0]),
      name: decodeURIComponent(segments[1]),
      version: decodeURIComponent(segments[2]),
    };

    if (segments[3] === 'tree') {
      return { kind: 'library', params };
    }

    if (segments[3] === 'browse') {
      return { kind: 'index-tree', params };
    }

    if (segments[3] === 'raw') {
      return { kind: 'raw', params };
    }

    if (segments[3] === 'query') {
      return { kind: 'query', params };
    }
  }

  return { kind: 'not-found' };
}

export function LLMContextApp() {
  const pathname = useAppPathname();
  const parsed = parseLcRoute(pathname);
  const { user, getToken, deploymentMode } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  // `/why` and `/argument` are SaaS-only marketing; `/docs` is actual API
  // documentation and renders in both deployments (with baseUrl templated
  // per-install).
  const route: LcRoute =
    deploymentMode === 'self-hosted' && (parsed.kind === 'why' || parsed.kind === 'argument')
      ? { kind: 'not-found' }
      : parsed;

  useEffect(() => {
    let cancelled = false;
    void Promise.resolve(user)
      .then((u) => (u ? getToken() : null))
      .then((token) => (token ? fetchMe(token) : null))
      .then((me) => {
        if (!cancelled) setIsAdmin(me?.isAdmin ?? false);
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, getToken]);

  // Org pages render without the tab bar
  if (route.kind === 'org-settings') {
    return <OrgSettingsPage />;
  }
  if (route.kind === 'org-new') {
    return <CreateOrgPage />;
  }
  // Marketing pages — SaaS-only (gated at the route level above).
  // /skills merged into the Get started page; DocsPage scrolls to the
  // agent-skill section when the path is /skills.
  if (route.kind === 'docs' || route.kind === 'skills') {
    return <DocsPage />;
  }
  if (route.kind === 'why') {
    return <WhyPage />;
  }
  if (route.kind === 'argument') {
    return <ArgumentPage />;
  }
  if (route.kind === 'contact') {
    return <ContactPage />;
  }

  // Library view, raw, and query render without the tab bar (they have their own back nav)
  if (route.kind === 'library') {
    return <PackageDetailPage params={route.params} isAdmin={isAdmin} />;
  }
  if (route.kind === 'index-tree') {
    return <IndexTreePage params={route.params} />;
  }
  if (route.kind === 'raw') {
    return <RawLlmTxtPage params={route.params} />;
  }
  if (route.kind === 'query') {
    return <QueryPage params={route.params} isAdmin={isAdmin} />;
  }
  if (route.kind === 'status') {
    return <StatusPage />;
  }

  return (
    <div className="space-y-0">
      {route.kind === 'home' ? (
        deploymentMode === 'self-hosted' ? (
          // Self-host: `/` shows the Docs page instead of the catalog.
          // Day-one self-hosts have an empty catalog, so leading with
          // setup + MCP config is more useful than an empty-state.
          // The catalog stays reachable via the "All Libraries" nav.
          <div data-testid="lc-page-home">
            <DocsPage />
          </div>
        ) : (
          <div data-testid="lc-page-home">
            <HomePage />
          </div>
        )
      ) : null}
      {route.kind === 'search' ? (
        <div data-testid="lc-page-search">
          <SearchPage />
        </div>
      ) : null}
      {route.kind === 'dashboard' ? (
        <div data-testid="lc-page-dashboard">
          <DashboardPage isAdmin={isAdmin} />
        </div>
      ) : null}
      {route.kind === 'request' ? (
        <div data-testid="lc-page-request">
          <RequestIndexPage isAdmin={isAdmin} />
        </div>
      ) : null}
      {route.kind === 'admin' ? (
        isAdmin ? (
          <PackagesListPage isAdmin={isAdmin} />
        ) : (
          <div className="py-16 text-center">
            <h2 className="text-lg font-semibold text-ink">Access denied</h2>
            <p className="mt-1 text-sm text-dim">You do not have admin access.</p>
            <div className="mt-4">
              <Button size="sm" onClick={() => navigateTo('/')}>
                Back home
              </Button>
            </div>
          </div>
        )
      ) : null}
      {route.kind === 'not-found' ? (
        <div className="py-16 text-center">
          <h2 className="text-lg font-semibold text-ink">Page not found</h2>
          <p className="mt-1 text-sm text-dim">The requested view does not exist.</p>
          <div className="mt-4">
            <Button size="sm" onClick={() => navigateTo('/')}>
              Back home
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
